import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@components/Typography';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Checkbox } from '@components/Checkbox';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import Autocomplete from '@components/Autocomplete';
import { alertActions, contactsActions } from '@actions';
import { contactsService } from '@services';
import { LabelType } from '@constants/labels';
import { useTranslation, useContactLocations, useIntlLocale } from '@hooks';
import { SEX_LABEL_KEYS, Sex, languageOptions } from '@helpers/contact-demographics';
import { handleApiError } from '@helpers/api';
import { Modal, Content, CheckboxWrapper } from './styles';

const BulkEditModal = ({ modal, setModal, selectedContacts, currentQuery, rowsPerPageRequests }) => {
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const [touchedCompanies, setTouchedCompanies] = useState([]);
    const [touchedLabels, setTouchedLabels] = useState([]);

    const { totalCount, bulkEditData: data } = useSelector(state => state.contacts);
    const { data: labels } = useSelector(state => state.labels);
    const dispatch = useDispatch();
    const { getLocationsOptions, mapSelectedLocationsToFormValue } = useContactLocations();
    useEffect(() => {
        dispatch(contactsActions.getLocations());
        dispatch(
            contactsActions.getBulkEditData({
                filter: {
                    ...currentQuery,
                    contacts: Object.keys(selectedContacts.list),
                    allContacts: selectedContacts.all,
                },
            }),
        );
    }, [dispatch]);

    useEffect(() => {
        if (Object.keys(data).length > 0) {
            setValue('companies', mapSelectedLocationsToFormValue(data.companies.map(i => i.id)), {
                shouldDirty: false,
            });
            setValue('labels', data.labels.map(i => i.id) || [], { shouldDirty: false });
            setValue('unsubscribed', data.unsubscribed || null, { shouldDirty: false });
        }
    }, [data, mapSelectedLocationsToFormValue]);

    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);
    const languages = useMemo(() => languageOptions(intlLocale), [intlLocale]);

    const selectedLength = Object.keys(selectedContacts.list).length;

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        reset,
        formState: { isDirty, isSubmitting },
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            contactId: t('ContactsPage.contactsSelected', {
                selectedContacts: selectedContacts.all ? totalCount : selectedLength,
            }),
            companies: [],
            unsubscribed: null,
            labels: [],
            sex: '',
            language: '',
        },
    });

    const locationsOptions = useMemo(() => getLocationsOptions(), [getLocationsOptions]);

    const onCloseModal = () => {
        setModal(false);
        dispatch(contactsActions.setBulkEditData({}));
        reset();
    };

    const getArrayDiff = (arr1, arr2) => {
        return arr1.filter(i => !arr2.includes(i));
    };

    const onSubmit = async () => {
        const values = getValues();
        const { companies, labels, unsubscribed, sex, language } = values;
        const dataCompanies = data.companies.map(i => i.id);
        const dataLabels = data.labels.map(i => i.id);
        const formattedSelectedCompanies = companies.map(i => i.id);
        try {
            await contactsService.updateBulkContacts({
                data: {
                    companiesToAdd: [
                        ...getArrayDiff(formattedSelectedCompanies, dataCompanies),
                        ...formattedSelectedCompanies.filter(i => touchedCompanies.includes(i)),
                    ],
                    companiesToRemove: getArrayDiff(dataCompanies, formattedSelectedCompanies),
                    labelsToAdd: [
                        ...getArrayDiff(labels, dataLabels),
                        ...labels.filter(i => touchedLabels.includes(i)),
                    ],
                    labelsToRemove: getArrayDiff(dataLabels, labels),
                    unsubscribed,
                    sex: sex === '' ? null : sex,
                    language: language || null,
                },
                filter: {
                    ...currentQuery,
                    contacts: Object.keys(selectedContacts.list),
                    allContacts: selectedContacts.all,
                },
            });
            dispatch(contactsActions.get(currentQuery, 0, rowsPerPageRequests));
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onCloseModal();
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        }
    };

    return (
        <Modal
            fullScreen
            isOpen={modal}
            handleClose={onCloseModal}
            isDirty={isDirty}
            title={t('ContactsPage.editContactsTitle')}
            subtitle={t('ContactsPage.editBulkContactsSubtitle')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isDirty}
        >
            <Content>
                <Controller
                    control={control}
                    name="contactId"
                    render={({ field, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('ContactsPage.contacts')}
                            fullWidth
                            disabled
                            sx={{ gridArea: 'input1' }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="companies"
                    render={({ field, ref }) => (
                        <Autocomplete
                            {...field}
                            ref={ref}
                            label={t('common.locations')}
                            options={locationsOptions}
                            value={field.value}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                            onChangeAutoComplete={items => {
                                const value = items.length ? items : [];
                                setValue('companies', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setTouchedCompanies(prev => {
                                    const touched = data.companies
                                        .map(i => i.id)
                                        .filter(i => !value.some(val => val.id === i));
                                    return [...prev, ...touched];
                                });
                            }}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                                partlyAssignedCallback: value =>
                                    data.companies
                                        .filter(i => !i.assignedForAll)
                                        .map(i => i.id)
                                        .includes(value) && !touchedCompanies.includes(value),
                            }}
                            required
                            className="companies-autocomplete"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="labels"
                    render={({ field, ref }) => (
                        <div style={{ gridArea: 'select2' }}>
                            <MultiSelectLabels
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('labels', value, { shouldDirty: true });
                                    setTouchedLabels(prev => {
                                        const touched = data.labels.map(i => i.id).filter(i => !value.includes(i));
                                        return [...prev, ...touched];
                                    });
                                }}
                                options={labelsOptions}
                                label={t('common.labels')}
                                fullWidth
                                type={LabelType.Contact}
                                onCreateCallback={newLabel => {
                                    const values = getValues();
                                    setValue('labels', [...values.labels, newLabel], {
                                        shouldDirty: true,
                                    });
                                }}
                                bulkEditData={data.labels}
                                touchedLabels={touchedLabels}
                            />
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name="sex"
                    render={({ field }) => (
                        <Select
                            {...field}
                            label={t('ContactsPage.sex')}
                            fullWidth
                            style={{ gridArea: 'sex' }}
                            onChange={event => setValue('sex', event.target.value, { shouldDirty: true })}
                            options={[
                                { value: '', label: t('ContactsPage.leaveUnchanged') },
                                ...Object.values(Sex).map(value => ({ value, label: t(SEX_LABEL_KEYS[value]) })),
                            ]}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="language"
                    render={({ field }) => (
                        <Select
                            {...field}
                            label={t('ContactsPage.language')}
                            fullWidth
                            style={{ gridArea: 'language' }}
                            onChange={event => setValue('language', event.target.value, { shouldDirty: true })}
                            options={[{ value: '', label: t('ContactsPage.leaveUnchanged') }, ...languages]}
                        />
                    )}
                />
                <>
                    <CheckboxWrapper>
                        <Controller
                            control={control}
                            name="unsubscribed"
                            render={({ field, ref }) => (
                                <Checkbox
                                    ref={ref}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                    onChange={e => setValue('unsubscribed', e.target.checked, { shouldDirty: true })}
                                    checked={field.value}
                                />
                            )}
                        />
                        <Typography variant="body2">{t('ContactsPage.unsubscribe')}</Typography>
                    </CheckboxWrapper>
                </>
            </Content>
        </Modal>
    );
};

export default BulkEditModal;
